import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher } from "@/api/client";
import { Sequence } from "../domain/Sequence";
import { ConfigurationRepository } from "../domain/ConfigurationRepository";
import { Configuration, InvoiceTemplate } from "../domain/Configuration";

function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Recursively convert nested object
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

export class ApiConfigurationRepository implements ConfigurationRepository {

    command_dispatcher: CommandDispatcher
    query_dispatcher: QueryDispatcher
    sync_dispatcher: CommandDispatcher
    constructor() {
        this.command_dispatcher = async_command_dispatcher
        this.query_dispatcher = query_dispatcher_get
        this.sync_dispatcher = sync_command_dispatcher
    }

    api_to_domain(configuration) {

        let config = new Configuration()
        config.company_id = configuration.company_id
        config.personalization_id = configuration.personalization_id

        // Only set personalization fields if they exist in the response
        // (they won't exist when loading ConfigurationByInvoiceableType from backend)
        if (configuration.name !== undefined) config.name = configuration.name
        if (configuration.path !== undefined) config.path = configuration.path
        if (configuration.path_url !== undefined) config.path_url = configuration.path_url
        if (configuration.footer !== undefined) config.footer = configuration.footer
        if (configuration.footer_small !== undefined) config.footer_small = configuration.footer_small
        if (configuration.alias !== undefined) config.alias = configuration.alias

        config.tax_type = configuration.tax_type
        config.sequences = []
        if (configuration.sequences && configuration.sequences.length > 0) {
            for (let x in configuration.sequences) {
                config.sequences.push(new Sequence(
                    configuration.sequences[x].id,
                    configuration.sequences[x].code,
                    configuration.sequences[x].current,
                    configuration.sequences[x].prefix,
                    configuration.sequences[x].suffix,
                    configuration.sequences[x].fill,
                    configuration.sequences[x].rectificative_code? configuration.sequences[x].rectificative_code : '',
                    configuration.sequences[x].rectificative_current? configuration.sequences[x].rectificative_current : 0,
                    configuration.sequences[x].rectificative_prefix? configuration.sequences[x].rectificative_prefix : '',
                    configuration.sequences[x].rectificative_suffix? configuration.sequences[x].rectificative_suffix : '',
                    configuration.sequences[x].rectificative_fill? configuration.sequences[x].rectificative_fill : '',
                    configuration.sequences[x].is_for_recurring_contracts,
                    configuration.company_id,
                    configuration.sequences[x].personalization_id,
                    configuration.sequences[x].next_number? configuration.sequences[x].next_number : '',
                    configuration.sequences[x].next_rectificative_number? configuration.sequences[x].next_rectificative_number : '',
                    configuration.sequences[x].min_number? configuration.sequences[x].min_number : -1,
                    configuration.sequences[x].min_inv_date? configuration.sequences[x].min_inv_date : '',
                    configuration.sequences[x].min_rectificative_number? configuration.sequences[x].min_rectificative_number : -1,
                    configuration.sequences[x].min_rectificative_inv_date? configuration.sequences[x].min_rectificative_inv_date : '',
                    configuration.sequences[x].deleted? configuration.sequences[x].deleted : false,
                    configuration.sequences[x].disabled? configuration.sequences[x].disabled : false
                ))
            }
        } else {
            config.sequences = [new Sequence()]
            config.sequences[0].company_id = configuration.company_id
        }
        config.email_template.message = configuration.email_template?.message
        config.email_template.subject = configuration.email_template?.subject

        // Handle invoice_template
        if (configuration.invoice_template) {
            config.invoice_template = new InvoiceTemplate(
                configuration.invoice_template.id,
                configuration.invoice_template.name,
                configuration.invoice_template.html,
                configuration.invoice_template.options,
                configuration.invoice_template.company_id
            )
        }

        config.hide_client_send_to_face = configuration.hide_client_send_to_face
        config.hide_client_send_to_faceb2b = configuration.hide_client_send_to_faceb2b
        config.hide_client_send_to_accounting = configuration.hide_client_send_to_accounting
        config.auto_send_to_verifactu = configuration.auto_send_to_verifactu
        config.remittance_only_iban = configuration.remittance_only_iban || false
        return config
    }

    domain_to_api(config: Configuration) {
        let res = JSON.parse(JSON.stringify(objectToDict(config)));
        if (res.hasOwnProperty('sequences')) {
            res['sequences'] = this.domain_to_api_sequences(config.sequences);
        }
        return res
    }

    domain_to_api_sequences(sequences: Sequence[]) {
        let res = []
        for (let x in sequences) {
            res.push(JSON.parse(JSON.stringify(objectToDict(sequences[x]))))
        }
        for (let i in res) {
            res[i]['current'] = Number.parseInt(res[i]['current'])
            res[i]['rectificative_current'] = Number.parseInt(res[i]['rectificative_current'])
        }
        return res
    }

    async get_configuration(company_id:Number, invoiceable_type: string, onlyEnabled?: boolean): Promise<Configuration> {
        let args = { 
            company_id: company_id,
            invoiceable_type: invoiceable_type,
            only_enabled: onlyEnabled
        }
        let config = await this.query_dispatcher.dispatch('get.einvoice.configuration', args)
        if (!config){
            return null
        }else {
            return this.api_to_domain(config)
        }
    }

    async save_configuration(configuration: Configuration, invoiceable_type:string) : Promise<any> {
        let configuration_data = this.domain_to_api(configuration)
        let args = {
            configuration: configuration_data,
            invoiceable_type: invoiceable_type
        }
        let res = await this.command_dispatcher.dispatch('save.personalized.fields', args)
        return res
    }

    async get_templates(company_id: Number): Promise<any> {
        let args = {
            company_id: company_id
        }
        let templates = await this.query_dispatcher.dispatch('get.templates', args)
        return templates
    }

    async update_companydb_configuration_flag(companydb_id: number, flag_name: string, flag_value: boolean): Promise<any> {
        let args = {
            company_id: companydb_id,
            flag_name: flag_name,
            flag_value: flag_value
        }
        return await this.sync_dispatcher.dispatch('facturae.update.company.configuration.flag', args)
    }
}